import React, { useEffect, useState, useCallback } from 'react';
import {
  Button,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  styled
} from '@mui/material';
import { dateFormatter, getReccurssiveObjectKeys } from '../../../../../utils';
import { Checkbox } from '../../../../../components';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(0.9),
  whiteSpace: 'nowrap',
  fontSize: '13px'
}));

export const StockManagementTable = ({
  data,
  columns,
  onRowClick,
  containerSX,
  sx,
  size,
  showInputField,
  handleInputChange,
  issLoading,
  fetchMoreData,
  hsnInputValues,
  batchInputValues,
  productInputValues,
  selectedSupplier,
  selectedLocation,
  pagination,
  showOk,
  isSelected,
  handleRowSelect,
  selectedRows
}) => {
  const [currentData, setCurrentData] = useState(data || []); // Holds current data
  const [itemCount, setItemCount] = useState(data.length); // Tracks the count of items displayed

  // Handles infinite scroll to fetch more data
  const onScroll = useCallback(
    (e) => {
      const scrollHeight = e.target.scrollHeight;
      const scrollTop = e.target.scrollTop;
      const clientHeight = e.target.clientHeight;


      const nearBottom = scrollHeight - scrollTop <= 350;
      if (!issLoading) {
        console.log(pagination.totalPages);
      }

      if (nearBottom && !issLoading && !(pagination.totalCount === 0)) {
        fetchMoreData(pagination.currentPage + 1, selectedLocation, selectedSupplier);
      }
    },
    [
      issLoading,
      pagination.currentPage,
      pagination.totalCount,
      pagination.totalPages,
      fetchMoreData,
      hsnInputValues,
      batchInputValues,
      productInputValues,
      selectedSupplier,
      selectedLocation
    ]
  );


  useEffect(() => {
    setCurrentData(data);
    setItemCount(data.length);
  }, [data]);

  const formatDateToMonth = (date) => {
    if (!date) return '';
    const dateObj = new Date(date);
    const year = dateObj.getFullYear();
    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0'); // Ensure two digits for the month
    return `${year}-${month}`;
  };
  return (
    <TableContainer sx={{ maxHeight: '259px', overflowY: 'auto', ...containerSX }} onScroll={onScroll}>
      <MuiTable sx={sx}>
        <TableHead>
          <TableRow sx={{ backgroundColor: 'lightblue' }}>
            {showOk && (
              <StyledTableCell key="ok-cell" sx={{ fontWeight: 'bold' }}>
                Edit
              </StyledTableCell>
            )}
            <StyledTableCell key="sr.no.-cell" sx={{ fontWeight: 'bold' }}>
              Sr. No.
            </StyledTableCell>
            {columns.map((column, index) => (
              <StyledTableCell sx={{ fontWeight: 'bold' }} key={`header-cell-${index}`}>
                {column.header}
              </StyledTableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, rowIndex) => (
            <TableRow
              {...(onRowClick && {
                onClick: () => {
                  onRowClick(row);
                }
              })}
              sx={{
                backgroundColor: rowIndex % 2 ? 'grey.200' : 'none',
                cursor: onRowClick ? 'pointer' : 'default',
                '&:hover': {
                  backgroundColor: 'grey.300'
                },
                transition: 'all 0.1s ease-in',
                backgroundColor: isSelected(row, 'ok') ? 'lightgreen' : 'none', // Highlight if selected
                '&:hover': { backgroundColor: 'grey.300' } // Hover effect
              }}
              key={row._id}
            >
              {showOk && (
                <StyledTableCell>
                  <Checkbox
                    sx={{
                      transform: 'scale(0.8)',
                      padding: '0'
                    }}
                    name={row?._id}
                    disabled={false}
                    checked={isSelected(row, 'ok')}
                    onChange={() => handleRowSelect(row, 'ok')}
                  />
                </StyledTableCell>
              )}
              <StyledTableCell key={`${row._id}-cell0`}>{rowIndex + 1}</StyledTableCell>
              {columns.map(({ header, accessor, sx }, colIndex) => (
                <StyledTableCell sx={sx} key={`${row._id}-cell${colIndex + 1}`}>
                  {header === 'bill_qty' && showInputField ? (
                    <TextField
                      value={row.productDetails?.[header] || ''}
                      onChange={(e) => handleInputChange(e, row._id, header)}
                      disabled={!selectedRows.some((selectedRow) => (selectedRow._id === row._id &&
                        selectedRow.productDetails.hsn_code === row.productDetails.hsn_code &&
                        selectedRow.productDetails.batch_no === row.productDetails.batch_no))}
                      variant="outlined"
                      size="small"
                      name="bill_qty"
                      sx={{
                        width: '60px',
                        '& .MuiOutlinedInput-input': {
                          padding: '4px 8px'
                        },
                        '& .MuiInputBase-root': {
                          padding: '0px'
                        }
                      }}
                    />
                  ) : header === 'mrp' && showInputField ? (
                    <TextField
                      value={row.productDetails?.[header] || ''}
                      onChange={(e) => handleInputChange(e, row._id, header)}
                      disabled={!selectedRows.some((selectedRow) => (selectedRow._id === row._id &&
                        selectedRow.productDetails.hsn_code === row.productDetails.hsn_code &&
                        selectedRow.productDetails.batch_no === row.productDetails.batch_no))}
                      variant="outlined"
                      size="small"
                      name="mrp"
                      sx={{
                        width: '60px',
                        '& .MuiOutlinedInput-input': {
                          padding: '4px 8px'
                        },
                        '& .MuiInputBase-root': {
                          padding: '0px'
                        }
                      }}
                    />
                  ) : header === 'batch_no' && showInputField ? (
                    <TextField
                      value={row.productDetails?.[header] || ''}
                      onChange={(e) => handleInputChange(e, row._id, header)}
                      disabled={!selectedRows.some((selectedRow) => (selectedRow._id === row._id &&
                        selectedRow.productDetails.hsn_code === row.productDetails.hsn_code &&
                        selectedRow.productDetails.batch_no === row.productDetails.batch_no))}
                      variant="outlined"
                      size="small"
                      name="supplier"
                      sx={{
                        width: '60px',
                        '& .MuiOutlinedInput-input': {
                          padding: '4px 8px'
                        },
                        '& .MuiInputBase-root': {
                          padding: '0px'
                        }
                      }}
                    />
                  ) : header === 'total_qty' && showInputField ? (
                    <TextField
                      value={row.productDetails?.[header]}
                      onChange={(e) => handleInputChange(e, row._id, header)}
                      disabled={!selectedRows.some((selectedRow) => (selectedRow._id === row._id &&
                        selectedRow.productDetails.hsn_code === row.productDetails.hsn_code &&
                        selectedRow.productDetails.batch_no === row.productDetails.batch_no))}
                      variant="outlined"
                      size="small"
                      name="total_qty"
                      sx={{
                        width: '60px',
                        '& .MuiOutlinedInput-input': {
                          padding: '4px 8px'
                        },
                        '& .MuiInputBase-root': {
                          padding: '0px'
                        }
                      }}
                    />
                  ) : header === 'exp_date' && showInputField ? (
                    <TextField
                      value={row.productDetails?.[header] ? formatDateToMonth(row.productDetails?.[header]) : ''}
                      onChange={(e) => handleInputChange(e, row._id, header)}
                      disabled={!selectedRows.some((selectedRow) => (selectedRow._id === row._id &&
                        selectedRow.productDetails.hsn_code === row.productDetails.hsn_code &&
                        selectedRow.productDetails.batch_no === row.productDetails.batch_no))}
                      variant="outlined"
                      type="month"
                      size="small"
                      name="exp_date"
                      sx={{
                        width: '120px',
                        '& .MuiOutlinedInput-input': {
                          padding: '4px 8px'
                        },
                        '& .MuiInputBase-root': {
                          padding: '0px'
                        }
                      }}
                    />
                  ) : typeof accessor === 'function' ? (
                    accessor(row) || '-'
                  ) : (
                    <>
                      {header === 'Created At' || header === 'Date'
                        ? dateFormatter(getReccurssiveObjectKeys(row, accessor)) || '-'
                        : getReccurssiveObjectKeys(row, accessor) || '-'}
                    </>
                  )}
                </StyledTableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </MuiTable>
    </TableContainer>
  );
};
